import { cn } from '@/lib/utils'

import { useBannerSliderVariants } from '../../../Theme'
import { LABELS } from '../../lib'

import type { ButtonHTMLAttributes, Ref } from 'react'

interface NavigationButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  direction: 'prev' | 'next'
  /** Size variant: 'banner' (default, larger) or 'product' (smaller with border) */
  size?: 'banner' | 'product'
  ref?: Ref<HTMLButtonElement>
}

/**
 * NavigationButton - Slider navigation arrow
 *
 * Features:
 * - Supports prev/next direction
 * - SVG arrow icons
 * - Accessible with aria-label
 * - Uses CVA variants via UiProvider
 * - React 19: Direct ref prop (no forwardRef)
 *
 * @example
 * <NavigationButton direction="prev" className="swiper-prev" />
 */
export function NavigationButton({
  direction,
  size = 'banner',
  className,
  ref,
  ...props
}: NavigationButtonProps) {
  const { bannerNavButtonVariants, bannerNavIconVariants } = useBannerSliderVariants()

  const ariaLabel = direction === 'prev' ? LABELS.NAV_PREV_SLIDE : LABELS.NAV_NEXT_SLIDE

  return (
    <button
      ref={ref}
      type="button"
      className={cn(bannerNavButtonVariants({ direction, size }), className)}
      aria-label={ariaLabel}
      {...props}
    >
      {direction === 'prev' ? (
        <svg
          className={bannerNavIconVariants()}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 19l-7-7 7-7"
          />
        </svg>
      ) : (
        <svg
          className={bannerNavIconVariants()}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 5l7 7-7 7"
          />
        </svg>
      )}
    </button>
  )
}
